import { UserOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  DatePicker,
  Flex,
  Popconfirm,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
} from "antd";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { debounce } from "lodash";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { LeaveType, StepStatus } from "../../enum/common";
import { ILeaveRequest, ILeaveRequestQuery } from "../../models/Hrm";
import { IPaginatedResponse } from "../../models/Pagination";
import { useReviewLeaveRequest } from "../../query/HrmQuery";
import { RootState } from "../../store";
import { notify } from "../../utils/toast";
import { getStatusTag, getTypeTag } from "../filter/FilterEnum";
import { getDisplayStatus } from "../filter/FilterUserOperation";
import { canReview } from "../helper/approval.helper";
import { ModalAddLeaveRequest } from "../modal/hrm/ModalAddLeaveRequest";
import { DepartmentCode } from "../../enum/roles";

const { Text } = Typography;
const { RangePicker } = DatePicker;
interface props {
  data: IPaginatedResponse<ILeaveRequest>;
  isLoading: boolean;
  isHasAvatars?: boolean;
  filter: ILeaveRequestQuery;
  setFilter: React.Dispatch<React.SetStateAction<ILeaveRequestQuery>>;
}
export const HrmLeaveRequestTable: React.FC<props> = ({
  data,
  filter,
  isHasAvatars = false,
  isLoading,
  setFilter,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const user = useSelector((state: RootState) => state.auth.user);
  const reviewLeaveRequest = useReviewLeaveRequest();
  const calculateTotalDays = (startDate: Date, endDate: Date) => {
    const days =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);

    return Math.ceil(days * 2) / 2;
  };
  const handleSubmit = async ({
    id,
    isApprove,
  }: {
    id: string;
    isApprove: boolean;
  }) => {
    try {
      await reviewLeaveRequest.mutateAsync({
        id,
        isApprove,
      });
      notify.success("Duyệt thành công");
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      notify.error(err.response?.data?.message ?? "Thao tác thất bại");
    }
  };

  const statusApproval = [
    { value: StepStatus.HR_PENDING, label: "Chờ duyệt" },
    { value: StepStatus.APPROVED, label: "Đã duyệt" },
    { value: StepStatus.REJECTED, label: "Từ chối" },
  ];
  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: keyof ILeaveRequestQuery, value?: string) => {
        setFilter((prev) => ({
          ...prev,
          [field]: value || undefined,
          page: 1,
        }));
      }, 300),
    [setFilter],
  );

  const handleSearch =
    (field: keyof ILeaveRequestQuery) => (value?: string) => {
      debouncedSearch(field, value);
    };

  const columns: TableProps<ILeaveRequest>["columns"] = [
    ...(isHasAvatars
      ? [
          {
            title: "Nhân viên",
            key: "employee",
            width: 250,
            render: (_: any, record: any) => (
              <Space size="small">
                <Avatar
                  icon={<UserOutlined />}
                  className="shrink-0 bg-blue-100 text-blue-600"
                  src={record?.createdByUser?.avatar}
                />

                <Flex vertical>
                  <Text strong className="truncate text-sm">
                    {record?.createdByUser?.fullName}
                  </Text>

                  <Text className="truncate text-xs">
                    {record?.createdByUser?.email}
                  </Text>
                </Flex>
              </Space>
            ),
          },
        ]
      : []),

    {
      title: "Thời gian",
      key: "time",
      width: 250,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <RangePicker
          showTime
          format="DD/MM/YYYY HH:mm"
          onChange={(dates) => {
            setFilter((prev) => ({
              ...prev,
              start: dates?.[0]?.format("YYYY-MM-DD HH:mm"),
              end: dates?.[1]?.format("YYYY-MM-DD HH:mm"),
            }));
            confirm?.();
          }}
        />
      ),
      render: (_, record) => (
        <Text strong>
          {dayjs(record.startDate).format("DD/MM/YYYY HH:mm")}
          {" -> "}
          {dayjs(record.endDate).format("DD/MM/YYYY HH:mm")}
        </Text>
      ),
    },

    {
      title: "Số ngày",
      dataIndex: "totalDays",
      key: "totalDays",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Text strong>
          {calculateTotalDays(
            new Date(record.startDate),
            new Date(record.endDate),
          )}
        </Text>
      ),
    },
    {
      title: "Loại nghỉ",
      dataIndex: "type",
      key: "type",
      width: 150,
      filteredValue: filter.type ? [filter.type] : null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <Select
          value={filter.type}
          placeholder="Chọn trạng thái"
          className="w-full"
          onChange={(value) => {
            const v = value ? String(value) : undefined;
            setSelectedKeys(v ? [v] : []);
            confirm?.();
            handleSearch("type")(v);
          }}
          options={[
            { value: LeaveType.EARLY, label: "Sớm" },
            { value: LeaveType.SUDDENTY, label: "Đột xuất" },
            { value: LeaveType.PLAN, label: "Có kế hoạch" },
          ]}
          allowClear
        />
      ),
      render: (type) => (
        <Tag
          color={getTypeTag(type).color}
          className="px-2 py-1 rounded-xl font-semibold"
        >
          {getTypeTag(type).label}
        </Tag>
      ),
    },

    {
      title: filter.status
        ? `Trạng thái (${getStatusTag(filter.status).label})`
        : "Trạng thái",
      width: 180,
      filteredValue: filter.status ? [filter.status] : null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <Select
          className="w-[150px]"
          placeholder="Chọn..."
          value={filter.status}
          onChange={(value) => handleSearch("status")(value)}
          allowClear
          options={statusApproval}
        />
      ),
      render: (_, record) => {
        const statusDisplay = getDisplayStatus(record);
        return (
          <Tag
            color={statusDisplay.color}
            className="px-2 py-1 rounded-xl font-semibold"
          >
            {statusDisplay.label}
          </Tag>
        );
      },
    },
    {
      title: "Lý do",
      dataIndex: "detail",
      key: "detail",
      ellipsis: true,
      width: 100,
    },
    {
      title: "Thao tác",
      key: "action",
      align: "center",
      width: 150,
      render: (_, record) => {
        const isCanReview = canReview(
          user!,
          record.status,
          record.createdByUser?.department?.departmentCode as DepartmentCode,
        );

        return (
          <Space direction="vertical" size="small">
            <Space>
              <Popconfirm
                title="Duyệt đơn nghỉ?"
                description="Xác nhận duyệt đơn nghỉ phép này?"
                okText="Duyệt"
                cancelText="Đóng"
                onConfirm={() =>
                  handleSubmit({
                    id: record.id,
                    isApprove: true,
                  })
                }
                disabled={!isCanReview}
              >
                <Button type="primary" disabled={!isCanReview}>
                  Duyệt
                </Button>
              </Popconfirm>

              <Popconfirm
                title="Từ chối đơn nghỉ?"
                description="Bạn có chắc muốn từ chối đơn này?"
                okText="Từ chối"
                cancelText="Đóng"
                onConfirm={() =>
                  handleSubmit({
                    id: record.id,
                    isApprove: false,
                  })
                }
                disabled={!isCanReview}
              >
                <Button danger disabled={!isCanReview}>
                  Từ chối
                </Button>
              </Popconfirm>
            </Space>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="flex-1  p-4 overflow-auto h-full">
      <Flex vertical gap="large" className="w-full">
        <Table
          scroll={{ x: 1000 }}
          columns={columns}
          dataSource={data?.data}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: data?.pagination.page,
            pageSize: data?.pagination.limit,
            total: data?.pagination.total,
            onChange: (page, pageSize) => {
              setFilter({
                ...filter,
                page: page,
                limit: pageSize,
              });
            },
          }}
          className="shadow-sm border border-gray-100 overflow-hidden"
          onRow={(record) => ({
            className: " hover:bg-blue-50/30",
          })}
        />
        <ModalAddLeaveRequest
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          query={filter}
        />
      </Flex>
    </div>
  );
};

export default HrmLeaveRequestTable;
